import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { DesignAxesCopy } from '../source-derived/axes-copy'
import type { DesignDecision } from './design-decisions'

/**
 * The third of the operator's three facts about a design system:
 *
 *   "시스템에서 제공하는 온톨로지, 프로젝트에서 정의된 온톨로지,
 *    프로젝트에서 정의하지 않아서 default 적용된 것 …
 *    그것이 컴포넌트 단위인지, theme 인지"
 *
 * The first two have carriers on this screen already — the axis rows say what
 * the source gave and what the creator chose. The third had NONE: measured on
 * `origin/main`, `describeDesignDecisions` computed a `provenance` of
 * `'project' | 'default' | 'overridden'` for all 263 resolved values on every
 * render, and every one of its screen-facing callers projected that field away
 * into a count before anything was drawn. Nothing on `/studio/design-system`
 * said that a value was in force because the project never chose it, and the
 * sentence occupying that space said the opposite ("이대로 두면 앱에 반영되지
 * 않습니다" — see `axes-copy.ts`, corrected in the same change).
 *
 * So this panel exists to carry `provenance === 'default'` to the screen, and
 * it renders nothing it did not read: every number is a `.filter().length`
 * over the decisions it is handed.
 *
 * ── Why the list is CLOSED by default ──────────────────────────────────────
 * Two reasons, and the first is the honest one: 263 rows of `--card-foreground`
 * is not what a creator opens this screen for — the COUNT is the fact, the
 * keys are the evidence you go to when you doubt it. The second is measured:
 * `i18n/__tests__/settings-and-design-system-residue.test.tsx` walks this
 * surface under `ko` and requires every Latin word on it to be a preset name.
 * CSS custom-property names (`--background`) and atom names (`accordion`) are
 * neither copy nor preset names, so rendering them unconditionally would put
 * this component permanently at odds with that gate. Behind a disclosure the
 * two facts coexist: the default render is copy-only, and the keys are one
 * click away.
 */
export interface UndecidedValuesPanelProps {
  /**
   * EVERY resolved value, decided or not — i.e. `describeDesignSystem().decisions`
   * unfiltered. Handed whole rather than pre-filtered so the denominator
   * ("N of M") is read from the same array as the numerator; a caller that
   * filtered first would have to count twice and could drift between them.
   */
  decisions: DesignDecision[]
  copy: DesignAxesCopy
}

export function UndecidedValuesPanel({ decisions, copy }: UndecidedValuesPanelProps) {
  const [open, setOpen] = useState(false)
  const undecided = decisions.filter((decision) => decision.provenance === 'default')
  // The sharper half of the fact. A default a control on this screen COULD
  // decide is an invitation; a default no control reaches is a limit of this
  // screen, and the creator has no way to discover it by clicking around.
  const unreachable = undecided.filter((decision) => decision.controlledBy === null).length
  const Chevron = open ? ChevronDown : ChevronRight

  return (
    <section
      data-testid="undecided-values"
      className="mt-3 rounded-md border border-border bg-muted/40 px-3 py-2"
    >
      <h3 className="text-xs font-semibold text-foreground">{copy.undecidedTitle}</h3>
      {undecided.length === 0 ? (
        <p className="mt-1 text-xs leading-5 text-muted-foreground">{copy.undecidedNone}</p>
      ) : (
        <>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            {copy.undecidedSummary(undecided.length, decisions.length)}
          </p>
          <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
            {copy.undecidedUnreachable(unreachable)}
          </p>
          <button
            type="button"
            data-testid="undecided-values-toggle"
            aria-expanded={open}
            onClick={() => setOpen((wasOpen) => !wasOpen)}
            className="mt-1.5 inline-flex min-h-10 items-center gap-1 text-xs font-semibold text-foreground"
          >
            <Chevron className="h-3.5 w-3.5" aria-hidden />
            {open ? copy.undecidedHide : copy.undecidedShow}
          </button>
          {open ? (
            <ul className="mt-1.5 flex max-h-64 flex-col gap-1 overflow-y-auto">
              {undecided.map((decision) => (
                <li
                  key={decision.key}
                  data-testid="undecided-value"
                  data-key={decision.key}
                  className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded border border-border bg-background px-2 py-1 text-xs"
                >
                  <code className="font-mono text-foreground">{decision.key}</code>
                  {/* (d) of the operator's list: per component, or theme-wide.
                      Reuses the scope headings the strip above already uses so
                      the two halves of the screen name the same thing the same
                      way. */}
                  <span className="text-muted-foreground">
                    {decision.scope === 'theme' ? copy.scopeTheme : copy.scopeComponent}
                  </span>
                  {/* And whether the creator has any way to change it here. */}
                  <span className="text-muted-foreground">
                    {decision.controlledBy
                      ? copy.axisLabels[decision.controlledBy]
                      : copy.undecidedNoControl}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}
    </section>
  )
}
